/**
 * Upload Context
 * Manages state for file upload batches (isolated from chat)
 */
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import uploadApiService, {
  BatchListItem,
  BatchStatusResponse,
  ActivityFeedItem,
  GlobalStatsResponse,
  BatchListParams,
} from '@/services/uploadApi';

interface UploadContextType {
  // State
  batches: BatchListItem[];
  currentBatch: BatchStatusResponse | null;
  activityFeed: ActivityFeedItem[];
  stats: GlobalStatsResponse | null;
  loading: boolean;
  error: string | null;
  
  // Pagination
  currentPage: number;
  totalPages: number;
  totalBatches: number;
  
  // Filters (stored to preserve during auto-refresh)
  currentFilters: BatchListParams;
  
  // Actions
  loadBatches: (params?: BatchListParams) => Promise<void>;
  loadBatchStatus: (batchId: string) => Promise<void>;
  loadActivityFeed: (limit?: number) => Promise<void>;
  loadStats: () => Promise<void>;
  refreshAll: () => Promise<void>;
  setCurrentPage: (page: number) => void;
  setCurrentFilters: (filters: BatchListParams) => void;
  clearError: () => void;
}

const UploadContext = createContext<UploadContextType | undefined>(undefined);

export const UploadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // State
  const [batches, setBatches] = useState<BatchListItem[]>([]);
  const [currentBatch, setCurrentBatch] = useState<BatchStatusResponse | null>(null);
  const [activityFeed, setActivityFeed] = useState<ActivityFeedItem[]>([]);
  const [stats, setStats] = useState<GlobalStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  
  // Filters (stored to preserve during auto-refresh)
  const [currentFilters, setCurrentFilters] = useState<BatchListParams>({});
  const currentFiltersRef = useRef<BatchListParams>({});
  
  // Keep ref in sync with state
  useEffect(() => {
    currentFiltersRef.current = currentFilters;
  }, [currentFilters]);
  
  // Load batches with pagination and filters
  const loadBatches = useCallback(async (params?: BatchListParams) => {
    setLoading(true);
    setError(null);
    
    // If params are provided (including empty object {}), use them and update stored filters
    // Otherwise, use stored filters from ref (always up-to-date)
    const filtersToUse = params !== undefined ? params : currentFiltersRef.current;
    
    try {
      const response = await uploadApiService.getBatchList({
        page: currentPage,
        limit: 20,
        ...filtersToUse,
      });
      
      // Merge intelligently: preserve completed batches and prevent regression
      // This prevents flickering when backend returns temporary incorrect data
      setBatches((prevBatches) => {
        const batchMap = new Map<string, BatchListItem>();
        
        // First, add all previous batches to map (preserve completed state)
        prevBatches.forEach((batch) => {
          batchMap.set(batch.batch_id, batch);
        });
        
        // Then, merge new batches intelligently
        response.batches.forEach((newBatch) => {
          const oldBatch = batchMap.get(newBatch.batch_id);
          
          if (oldBatch) {
            // If old batch was completed, preserve completed status and correct file counts
            if (oldBatch.status === 'completed' && oldBatch.completed_at) {
              // Only update if new batch is also completed or failed
              if (newBatch.status === 'completed' || newBatch.status === 'failed') {
                batchMap.set(newBatch.batch_id, newBatch);
              } else {
                // Preserve old completed batch to prevent regression
                // Also preserve files_processed and total_files if they were correct
                batchMap.set(newBatch.batch_id, {
                  ...oldBatch,
                  // Preserve files_processed and total_files from old batch (they were correct)
                  files_processed: oldBatch.files_processed,
                  total_files: oldBatch.total_files,
                });
              }
            } else {
              // For non-completed batches, always update with new data
              batchMap.set(newBatch.batch_id, newBatch);
            }
          } else {
            // New batch, add it
            batchMap.set(newBatch.batch_id, newBatch);
          }
        });
        
        // Convert map back to array, maintaining order from response but removing duplicates
        // Use a Set to track seen batch_ids to prevent duplicates
        const seenBatchIds = new Set<string>();
        const mergedBatches: BatchListItem[] = [];
        
        // Process response.batches in order, but only add each batch_id once
        // Use batchMap.values() to ensure we get deduplicated batches
        for (const batch of response.batches) {
          if (!seenBatchIds.has(batch.batch_id)) {
            seenBatchIds.add(batch.batch_id);
            const batchToAdd = batchMap.get(batch.batch_id) || batch;
            mergedBatches.push(batchToAdd);
          }
        }
        
        // Also add any batches from batchMap that weren't in response.batches
        // This ensures we don't lose batches that were in previous state
        for (const batch of batchMap.values()) {
          if (!seenBatchIds.has(batch.batch_id)) {
            seenBatchIds.add(batch.batch_id);
            mergedBatches.push(batch);
          }
        }
        
        return mergedBatches;
      });
      
      setTotalPages(response.pages);
      setTotalBatches(response.total);
      setCurrentPage(response.page);
      
      // Always update stored filters when loadBatches is called with explicit params
      // If params is undefined, keep currentFilters, otherwise update
      if (params !== undefined) {
        setCurrentFilters(filtersToUse);
      }
      
      console.log('✅ Batches loaded:', response.batches.length, 'with filters:', filtersToUse);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to load batches';
      setError(errorMessage);
      console.error('❌ Failed to load batches:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage]);
  
  // Load specific batch status
  const loadBatchStatus = useCallback(async (batchId: string) => {
    // Don't set loading to true if we already have the batch (to avoid flickering)
    const wasLoading = loading;
    if (!currentBatch || currentBatch.batch_id !== batchId) {
      setLoading(true);
    }
    setError(null);
    
    try {
      const response = await uploadApiService.getBatchStatus(batchId);
      
      // Use functional update to avoid stale closure when polling rapidly
      setCurrentBatch((prev) => {
        if (!prev || prev.batch_id !== batchId || !prev.files) {
          return response;
        }
        // Merge: only preserve "completed" when backend sends inconsistent downgrade
        const mergedFiles = response.files.map((newFile) => {
          const oldFile = prev.files!.find((f) => f.id === newFile.id);
          if (
            oldFile?.status === 'completed' &&
            oldFile?.completed_at &&
            !newFile.completed_at &&
            newFile.status !== 'completed' &&
            newFile.status !== 'failed'
          ) {
            return { ...newFile, status: 'completed' as const, completed_at: oldFile.completed_at };
          }
          if (newFile.completed_at && newFile.status !== 'completed') {
            return { ...newFile, status: 'completed' as const };
          }
          return newFile;
        });
        return { ...response, files: mergedFiles };
      });
      
      console.log('✅ Batch status loaded:', batchId);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to load batch status';
      setError(errorMessage);
      console.error('❌ Failed to load batch status:', err);
    } finally {
      if (!wasLoading || !currentBatch || currentBatch.batch_id !== batchId) {
        setLoading(false);
      }
    }
  }, [currentBatch, loading]);
  
  // Load activity feed
  const loadActivityFeed = useCallback(async (limit: number = 100) => {
    try {
      const response = await uploadApiService.getActivityFeed(limit);
      setActivityFeed(response.activities);
      console.log('✅ Activity feed loaded:', response.activities.length);
    } catch (err: any) {
      console.error('❌ Failed to load activity feed:', err);
      // Don't set error for activity feed (non-critical)
    }
  }, []);
  
  // Load global stats
  const loadStats = useCallback(async () => {
    try {
      const response = await uploadApiService.getGlobalStats();
      setStats(response);
      console.log('✅ Stats loaded');
    } catch (err: any) {
      console.error('❌ Failed to load stats:', err);
      // Don't set error for stats (non-critical)
    }
  }, []);
  
  // Refresh all data (preserves current filters)
  const refreshAll = useCallback(async () => {
    // Use ref to get current filters at call time (always up-to-date)
    // Pass undefined to loadBatches so it uses currentFiltersRef.current
    await Promise.all([
      loadBatches(), // Pass undefined to use currentFiltersRef.current
      loadActivityFeed(),
      loadStats(),
    ]);
  }, [loadBatches, loadActivityFeed, loadStats]);
  
  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);
  
  // Initial load
  useEffect(() => {
    console.log('🔄 UploadContext: Initial load');
    refreshAll();
    
    // Auto-refresh every 5 seconds for batch list and activity feed
    // Note: BatchDetails page has its own 2-second refresh for real-time progress
    const interval = setInterval(() => {
      console.log('🔄 UploadContext: Auto-refresh');
      refreshAll();
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Reload batches when page changes (preserves filters)
  useEffect(() => {
    if (currentPage > 0) { // Only reload if page is set
      loadBatches(); // Pass undefined to use currentFiltersRef.current
    }
  }, [currentPage, loadBatches]);
  
  const value: UploadContextType = {
    batches,
    currentBatch,
    activityFeed,
    stats,
    loading,
    error,
    currentPage,
    totalPages,
    totalBatches,
    currentFilters,
    loadBatches,
    loadBatchStatus,
    loadActivityFeed,
    loadStats,
    refreshAll,
    setCurrentPage,
    setCurrentFilters,
    clearError,
  };
  
  return (
    <UploadContext.Provider value={value}>
      {children}
    </UploadContext.Provider>
  );
};

/**
 * Hook to use Upload context
 */
export const useUpload = (): UploadContextType => {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error('useUpload must be used within UploadProvider');
  }
  return context;
};

export default UploadContext;

